import { eventStatus } from '@/constants/event'
import type { DisplayEvent, Event, EventTagType } from '@/schemas/event.schema'
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import type { Category } from '@/schemas/category.schema'
import type { Tags } from '@/schemas/tag.schema'
import {
  PAYMENT_STATUS,
  PROTECTED_ROLES,
  PROTECTION_MESSAGES,
  USER_ROLES,
} from '@/constants'
import type {
  Attendee,
  AttendeeBookingsSchema,
  DisplayAttendeeBookingsSchema,
} from '@/schemas/attendee.schema'
import type { DisplayAttendee } from '@/components/attendee/attendee-columns'
import axios from 'axios'
import { showErrorToast } from '@/components/ui/toast'
import { tokenService } from '@/lib/token.service'
import type {
  ApiUserType,
  DisplayUser,
  DisplayUserAttendedEvent,
  UserAttendedEvent,
  User as UserProfile,
} from '@/schemas/user.schema'
import { PATHROUTES } from '@/constants/path'
import { format, subDays } from 'date-fns'
import { SPONSOR_REPORT_TYPES } from '@/constants/sponsor'
import { DISPLAY_SPONSORSHIP_STATUS } from '@/schemas/sponsor.schems'
import DOMPurify from 'dompurify'
import { attendeePersona, attendeeSkiExperience } from '@/constants/attendee'

export const transformUserData = (apiUser: ApiUserType): DisplayUser => {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    const date = new Date(dateStr)
    return date
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
      .replace(',', ',')
  }

  const getStatusDisplay = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'Active'
      case 'suspended':
        return 'Suspended'
      case 'banned':
        return 'Banned'
      default:
        return 'Active'
    }
  }

  const getPrimaryRole = (roles: ApiUserType['userRoles']) => {
    if (roles.length === 0) return 'User'
    // Return the first role's title, or prioritize super_admin
    const superAdmin = roles.find(role => role.name === 'super_admin')
    return superAdmin ? superAdmin.title : roles[0].title
  }

  return {
    ...apiUser,
    id: apiUser.id,
    firstName: apiUser.firstName,
    lastName: apiUser.lastName,
    email: apiUser.email,
    phoneNumber: apiUser.phoneNumber ?? '',
    status: getStatusDisplay(apiUser.status) as
      | 'active'
      | 'suspended'
      | 'banned',
    lastActiveAt: apiUser.lastActiveAt,
    role: getPrimaryRole(apiUser.userRoles),
    userRoles: apiUser.userRoles,
    createdAt: apiUser.createdAt ?? '',
    updatedAt: apiUser.updatedAt ?? '',
    events: apiUser.events ?? 0,
    lastActive: formatDate(apiUser.lastActiveAt),
    suspendedUntil: apiUser.suspendedUntil ?? null,
    suspensionReason: apiUser.suspensionReason ?? '',
    adminNotes: apiUser.adminNotes ?? '',
  }
}

// Add this function after imports (Line 30)
export type RoleData = {
  id: string | number
  name: string
  title: string
}

export const transformRolesToOptions = (
  rolesData: { data?: RoleData[] } | null
) => {
  if (!rolesData) {
    return []
  }

  if (!rolesData.data) {
    return []
  }

  if (!Array.isArray(rolesData.data)) {
    return []
  }

  // Transform array
  const options = rolesData.data.map((role: RoleData) => ({
    id: String(role.id),
    name: role.name,
    title: role.title,
  }))

  return options
}

export const formatGrowth = (percentage: number) => {
  return {
    value: `${percentage >= 0 ? '+' : ''}${percentage}%`,
    isPositive: percentage >= 0,
  }
}
export const validatePhoneNumber = (phone: string): string => {
  if (!phone) return ''

  try {
    const phoneNumber = parsePhoneNumber(phone)
    if (!phoneNumber) {
      const countryCodePattern = /^\+\d{1,4}$/
      if (countryCodePattern.test(phone.trim())) {
        return 'Please enter complete phone number'
      }
      return 'Invalid phone number format'
    }

    if (!isValidPhoneNumber(phone)) {
      return 'Please enter a valid phone number'
    }

    return '' // No error
  } catch {
    return 'Invalid phone number format'
  }
}

export const formatDateTimeIntl = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

export const formatDate = (dateStr: string | null) => {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  return date
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    .replace(',', ',')
}

export const transformEventData = (apiEvent: Event): DisplayEvent => {
  const getStatusDisplay = (status: string) => {
    if (status in eventStatus) {
      return eventStatus[status as keyof typeof eventStatus]
    }
    return ''
  }

  const sponsors = apiEvent?.sponsors.map(sponsor => {
    const firstName = sponsor?.user?.firstName || ''
    const lastName = sponsor?.user?.lastName || ''
    return `${firstName} ${lastName}`
  })

  return {
    id: apiEvent.id,
    name: apiEvent.name,
    organizerName: apiEvent.organizers.join(','),
    location: apiEvent.location,
    category: apiEvent.category?.name ?? 'N/A',
    startDateTime: apiEvent.startDateTime
      ? formatDate(apiEvent.startDateTime)
      : 'N/A',
    endDateTime: apiEvent.endDateTime
      ? formatDate(apiEvent.endDateTime)
      : 'N/A',
    status: getStatusDisplay(apiEvent.status),
    type: apiEvent.type,
    attendeesCount: apiEvent.attendeesCount,
    tickets: apiEvent.tickets,
    sponsors: sponsors.join(', ') || 'N/A',
    subEventConstraint: apiEvent?.subEventConstraint,
  }
}

export interface CategoryData {
  id: string
  name: string
}

export const transformCategoriesToOptions = (
  categoryData: CategoryData[] | undefined
) => {
  if (!categoryData) {
    return []
  }

  // Transform array
  const options = categoryData?.map((category: CategoryData) => ({
    id: String(category.id),
    name: category.name,
    title: category.name,
  }))

  return options
}
interface NameFormData {
  firstName: string
  lastName: string
}

export const validateNames = (formData: NameFormData): string[] => {
  const errors: string[] = []

  // First Name validation
  if (!formData.firstName.trim()) {
    errors.push('First name is required')
  } else if (formData.firstName.length > 255) {
    errors.push('First name cannot exceed 255 characters')
  }

  // Last Name validation
  if (!formData.lastName.trim()) {
    errors.push('Last name is required')
  } else if (formData.lastName.length > 255) {
    errors.push('Last name cannot exceed 255 characters')
  }

  return errors
}

export const validateEmail = (email: string): string => {
  if (!email.trim()) {
    return 'Email is required'
  }

  if (email.length > 255) {
    return 'Email cannot exceed 255 characters'
  }

  // Email regex pattern
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address'
  }

  return '' // No error
}

export interface ProtectionCheck {
  canPerformAction: boolean
  reason?: string
}

export const checkUserActionPermission = (
  targetUser: ApiUserType,
  currentUserId: string
): ProtectionCheck => {
  if (targetUser.id === currentUserId) {
    return {
      canPerformAction: false,
      reason: PROTECTION_MESSAGES.SELF_ACTION,
    }
  }

  const isSlopeSyncAdmin = targetUser.userRoles?.some(
    role =>
      role.name === PROTECTED_ROLES.SUPER_ADMIN ||
      role.title === PROTECTED_ROLES.SLOPESYNC_ADMIN
  )

  if (isSlopeSyncAdmin) {
    return {
      canPerformAction: false,
      reason: PROTECTION_MESSAGES.SUPER_ADMIN_PROTECTION,
    }
  }

  return {
    canPerformAction: true,
  }
}

export const formatAddress = (address?: {
  street?: string
  city?: string
  state?: string
  country?: string
  zipCode?: string
}): string => {
  if (!address) return 'N/A'

  const parts = [
    address.street,
    address.city,
    address.state,
    address.country,
    address.zipCode,
  ].filter(Boolean)

  return parts.length ? parts.join(', ') : 'N/A'
}

export const capitalizeString = (str: string) => {
  if (!str) {
    return str
  }

  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export const transformCategoryData = (apiEvent: Category): Category => {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    const date = new Date(dateStr)
    return date
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
      .replace(',', ',')
  }

  return {
    id: apiEvent.id,
    name: apiEvent.name,
    createdAt: formatDate(apiEvent.createdAt),
    description: apiEvent.description,
    events: apiEvent.events,
    status: apiEvent.status,
  }
}

export const transformTagData = (apiEvent: Tags): Tags => {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    const date = new Date(dateStr)
    return date
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
      .replace(',', ',')
  }

  return {
    id: apiEvent.id,
    name: apiEvent.name,
    createdAt: formatDate(apiEvent.createdAt),
    events: apiEvent.events,
    status: apiEvent.status,
    isTrending: apiEvent.isTrending,
  }
}
export interface TagData {
  id: string
  name: string
}

export const transformTagToOptions = (tagData: TagData[] | undefined) => {
  if (!tagData) {
    return []
  }

  const options = tagData?.map((tag: TagData) => ({
    id: String(tag.id),
    name: tag.name,
    title: tag.name,
  }))

  return options
}

export const extractEventTags = (tags: EventTagType[]) => {
  return tags
    .map(tagWrapper => tagWrapper?.tag)
    .filter(tag => !!tag?.id && !!tag?.name)
}

export const transformAttendeeData = (attendee: Attendee): DisplayAttendee => {
  const ticketType =
    Array.isArray(attendee?.items) && attendee.items[0]?.ticket?.name
      ? String(attendee.items[0].ticket.name)
      : ''

  const purchaseDate = attendee?.createdAt
    ? new Date(attendee.createdAt).toLocaleString()
    : ''

  return {
    id: attendee.id,
    name: `${attendee.user.firstName} ${attendee.user.lastName}`,
    eventName: attendee.event?.name,
    email: attendee?.user?.email,
    ticketType,
    status: attendee.status,
    purchaseDate,
    totalPaid: attendee.totalAmount,
    baseAmount: attendee.baseAmount,
    feeAmount: attendee.feeAmount,
    pendingAddonBookings: attendee.pendingAddonBookings || 0,
  }
}

export const transformAttendeeBookingData = (
  booking: AttendeeBookingsSchema
): DisplayAttendeeBookingsSchema => {
  const experience =
    attendeeSkiExperience[
      booking.experience as keyof typeof attendeeSkiExperience
    ] || 'N/A'

  const persona =
    attendeePersona[booking.persona as keyof typeof attendeePersona] || 'N/A'

  return {
    id: booking.id,
    name: booking.name,
    email: booking.email,
    experience: experience,
    persona: persona,
    eventName: booking.event?.name || '',
    isSignedConsentForm: booking.isConsentFormSigned,
    consentFormSignedAt: booking.consentFormSignedAt || '',
    signedConsentFormUrl: booking.signedConsentFormUrl,
    status: booking.status,
  }
}

export const handleAxiosError = (
  title: string,
  error: unknown,
  defaultError?: string
) => {
  if (axios.isAxiosError(error)) {
    const message = error?.response?.data?.message || defaultError || title
    showErrorToast(title, message)
  } else {
    showErrorToast(title, 'An unexpected error occurred')
  }
}

/**
 * Handles Axios errors where the response body is a Blob (e.g. when the request
 * was made with `responseType: 'blob'`). Parses the Blob as JSON to extract the
 * server's error message and shows an error toast.
 *
 * @param title - The toast title / fallback message shown when no server message is available.
 * @param error - The unknown error thrown by Axios.
 */
export const handleBlobAxiosError = async (
  title: string,
  error: unknown
): Promise<void> => {
  if (!axios.isAxiosError(error)) {
    showErrorToast(title, 'An unexpected error occurred')
    return
  }

  if (error.response?.data instanceof Blob) {
    try {
      const text = await error.response.data.text()
      const json = JSON.parse(text) as { message?: string }
      showErrorToast(title, json.message ?? title)
    } catch {
      showErrorToast(title)
    }
  } else {
    const message =
      (error.response?.data as { message?: string })?.message ?? title
    showErrorToast(title, message)
  }
}

export const formatTicketPriceWithFee = (
  totalPaid: string | number
): string => {
  const paid =
    typeof totalPaid === 'string' ? Number.parseFloat(totalPaid) : totalPaid
  if (Number.isNaN(paid)) return ''
  const fee = +(paid * 0.03).toFixed(2)
  const ticketPrice = +(paid - fee).toFixed(2)
  return `$${ticketPrice.toFixed(2)} + $${fee.toFixed(2)} fee`
}

export const paymentStatusClasses = (status: string) => {
  switch (status) {
    case PAYMENT_STATUS.COMPLETED:
    case PAYMENT_STATUS.COMPLETED_MANUALLY:
    case DISPLAY_SPONSORSHIP_STATUS.PAID:
      return 'bg-ss-positive-50 text-ss-positive-200 border-ss-positive-100'
    case PAYMENT_STATUS.FAILED:
    case PAYMENT_STATUS.CANCELLED:
    case DISPLAY_SPONSORSHIP_STATUS.FAILED:
    case DISPLAY_SPONSORSHIP_STATUS.CANCELLED:
      return 'bg-ss-critical-50 text-ss-critical-200 border-ss-critical-100'
    case PAYMENT_STATUS.PENDING:
      return 'bg-ss-warning-50 text-ss-warning-200 border-ss-warning-100'
    case PAYMENT_STATUS.REFUNDED:
      return 'bg-ss-brand-50 text-ss-blue-500 border-ss-blue-100'
    case DISPLAY_SPONSORSHIP_STATUS.SPONSORSHIP_REQUESTED:
      return 'bg-ss-warning-50 text-ss-warning-200 border-ss-warning-100'
    case DISPLAY_SPONSORSHIP_STATUS.NOT_SELECTED:
      return 'bg-ss-action-gray-50 text-ss-action-gray-200 border-ss-action-gray-100'
    default:
      return ''
  }
}

export const formatPaymentStatusText = (status: string) => {
  if (status === PAYMENT_STATUS.COMPLETED_MANUALLY) {
    return 'completed (manually)'
  }
  return status
}

export const safeDownloadFile = ({
  data,
  fileName,
}: {
  data: Blob
  fileName: string
}) => {
  if (!(data instanceof Blob)) {
    return
  }

  const url = URL.createObjectURL(data)

  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()

  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export const isUserSuperAdmin = () => {
  const user: UserProfile = tokenService.getUser()
  return user?.userRoles.some(role => role.name === USER_ROLES.superAdmin)
}

type DisableDateOptions = {
  dateToCheck: Date
  eventStartDate: Date
  eventEndDate: Date
  minDate?: Date
  maxDate?: Date
}

export const isDisableDate = ({
  dateToCheck,
  eventStartDate,
  eventEndDate,
  minDate,
  maxDate,
}: DisableDateOptions): boolean => {
  if (dateToCheck <= eventStartDate) return true
  if (dateToCheck > eventEndDate) return true
  if (minDate && dateToCheck < minDate) return true
  if (maxDate && dateToCheck > maxDate) return true
  return false
}

export const getDefaultRedirectPath = () => {
  const user: UserProfile = tokenService.getUser()
  const userRole = user?.userRoles[0]?.name

  switch (userRole) {
    case USER_ROLES.superAdmin:
      return PATHROUTES.DASHBOARD.USERS_MANAGEMENT
    case USER_ROLES.eventAdmin:
      return PATHROUTES.DASHBOARD.EVENT_DASHBOARD
    case USER_ROLES.sponsor:
      return PATHROUTES.DASHBOARD.SPONSORSHIP_MANAGEMENT
    default:
      return PATHROUTES.DASHBOARD.ROOT
  }
}

export function getReportDateRange(
  reportType: (typeof SPONSOR_REPORT_TYPES)[keyof typeof SPONSOR_REPORT_TYPES]
): {
  startDate: string
  endDate: string
} {
  const now = new Date()

  const daysMap: Record<string, number> = {
    [SPONSOR_REPORT_TYPES.LAST_10_DAYS]: 10,
    [SPONSOR_REPORT_TYPES.LAST_30_DAYS]: 30,
    [SPONSOR_REPORT_TYPES.LAST_90_DAYS]: 90,
  }

  const days = daysMap[reportType] ?? 10
  const start = subDays(now, days)

  return {
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(now, 'yyyy-MM-dd'),
  }
}

export const calculateRefundAmount = (
  totalAmount: string | number,
  refundPercent: number
): string => {
  const amount =
    typeof totalAmount === 'string'
      ? Number.parseFloat(totalAmount)
      : totalAmount
  if (Number.isNaN(amount)) return '$0.00'
  const refundAmount = (amount * refundPercent) / 100
  return `$${refundAmount.toFixed(2)}`
}

export const STATUSES = {
  OPEN: 'Open',
  SUSPENDED: 'suspended',
  SOLD_OUT: 'sold out',
  CLOSED: 'closed',
  CANCELLED: 'cancelled',
  BANNED: 'banned',
  PENDING: 'pending',
  DRAFT: 'draft',
  ACTIVE: 'active',
}

export const getStatusBadgeClass = (status: string | undefined) => {
  switch (status) {
    case STATUSES.OPEN:
      return 'bg-ss-positive-50 text-ss-positive-200 border-ss-positive-100 hover:bg-ss-positive-50'
    case STATUSES.SUSPENDED:
      return 'bg-ss-warning-50 text-ss-warning-200 border-ss-warning-100 hover:bg-ss-warning-50'
    case STATUSES.SOLD_OUT:
      return 'bg-ss-brand-50 text-ss-brand-200 border-ss-brand-100 hover:bg-ss-brand-50'
    case STATUSES.CLOSED:
    case STATUSES.CANCELLED:
    case STATUSES.BANNED:
      return 'bg-ss-critical-50 text-ss-critical-200 border-ss-critical-100 hover:bg-ss-critical-50'
    case STATUSES.PENDING:
    case STATUSES.DRAFT:
      return 'bg-ss-warning-50 text-ss-warning-200 border-ss-warning-100 hover:bg-ss-warning-50'
    case STATUSES.ACTIVE:
      return 'bg-ss-positive-50 text-ss-positive-200 border-ss-positive-100 hover:bg-ss-positive-50'
    default:
      return 'bg-ss-action-gray-50 text-ss-neutral-600 border-ss-neutral-50 hover:bg-ss-action-gray-50'
  }
}

export const transformUserAttendedEvents = (
  events: UserAttendedEvent
): DisplayUserAttendedEvent => {
  return {
    id: events?.id,
    eventName: events?.event.name,
    startDateTime: events?.event.startDateTime,
    status: events?.event?.status,
    totalAttendees: events?.totalAttendees,
    type: events?.event?.type,
  }
}

export const formatDateIntl = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
}

export const getFormattedAddresses = (
  place: google.maps.places.PlaceResult,
  components: google.maps.GeocoderAddressComponent[]
) => {
  const componentMap = new Map<string, string>()
  components.forEach(comp => {
    comp.types.forEach(type => {
      if (!componentMap.has(type)) {
        componentMap.set(type, comp.long_name)
      }
    })
  })

  const streetNumber = componentMap.get('street_number') || ''
  const route = componentMap.get('route') || ''
  const premise = componentMap.get('premise') || ''
  const subpremise = componentMap.get('subpremise') || ''
  const neighborhood = componentMap.get('neighborhood') || ''
  const sublocality =
    componentMap.get('sublocality') ||
    componentMap.get('sublocality_level_1') ||
    componentMap.get('sublocality_level_2') ||
    componentMap.get('sublocality_level_3') ||
    ''
  const country = componentMap.get('country') || ''
  const state = componentMap.get('administrative_area_level_1') || ''
  const city =
    componentMap.get('locality') ||
    componentMap.get('administrative_area_level_2') ||
    ''
  const zipCode = componentMap.get('postal_code') || ''

  const streetAddressParts: string[] = []

  if (premise) streetAddressParts.push(premise)

  if (streetNumber || route) {
    streetAddressParts.push([streetNumber, route].filter(Boolean).join(' '))
  }

  if (subpremise) streetAddressParts.push(subpremise)

  if (sublocality) streetAddressParts.push(sublocality)

  if (neighborhood && !streetAddressParts.includes(neighborhood)) {
    streetAddressParts.push(neighborhood)
  }

  const isPOI = place.types?.some(type =>
    [
      'point_of_interest',
      'establishment',
      'store',
      'restaurant',
      'cafe',
      'bar',
      'shopping_mall',
      'park',
      'museum',
      'airport',
      'train_station',
      'transit_station',
      'stadium',
      'school',
      'university',
      'hospital',
      'library',
      'gym',
    ].includes(type)
  )

  let placeName = ''
  if (isPOI && place.name) {
    placeName = place.name
  }

  let finalAddress = streetAddressParts.filter(Boolean).join(', ')

  const streetAddress = [streetNumber, route].filter(Boolean).join(' ')
  if (
    placeName &&
    placeName !== streetAddress &&
    !finalAddress.includes(placeName) &&
    !placeName.match(/^\d+\s/)
  ) {
    finalAddress = `${placeName}, ${finalAddress}`
  }

  return {
    manual_formatted_Address: finalAddress,
    country,
    state,
    city,
    zipCode,
  }
}

export const getPlainText = (htmlContent: string): string => {
  if (!htmlContent) return ''

  const sanitizedHtml = DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  })

  const tempDiv = document.createElement('div')
  try {
    tempDiv.innerHTML = sanitizedHtml
    const plainText = tempDiv.textContent || tempDiv.innerText || ''
    return plainText
  } finally {
    tempDiv.remove()
  }
}

export const getPlainTextLength = (htmlContent: string): number => {
  if (!htmlContent) return 0

  const plainText = getPlainText(htmlContent)

  return plainText.trim().length
}

export const formatCurrency = (amount: number | string, currency: string) => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(numAmount)
}

/**
 * Safely extracts error message from backend response
 * @param error - The error object from API call
 * @param fallbackMessage - Fallback message if extraction fails
 * @returns Extracted error message or fallback message
 */
export const getBackendErrorMessage = (
  error: unknown,
  fallbackMessage = 'An unexpected error occurred'
): string => {
  // Check if it's an Axios error with response data
  if (axios.isAxiosError(error)) {
    const message = error?.response?.data?.message
    if (typeof message === 'string' && message.trim()) {
      return message
    }
  }

  // Check if it's a standard Error object
  if (error instanceof Error) {
    return error.message || fallbackMessage
  }

  // Fallback for any other error type
  return fallbackMessage
}
